import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSwitcher from '../components/LanguageSwitcher';

const LAST_UPDATED = '15 April 2026';
const CONTROLLER = 'nordX Labs Oy';
const BUSINESS_ID = '3615236-4';
const CONTACT = 'hello@animlearn.com';

const content = {
  en: {
    title: 'Terms of Service',
    updated: `Last updated: ${LAST_UPDATED}`,
    back: 'Back',
    sections: [
      {
        title: '1. About AnimLearn',
        body: `AnimLearn is an AI-powered educational platform that generates animated learning videos and adaptive practice exercises. It is operated by **${CONTROLLER}** (Business ID: ${BUSINESS_ID}), registered in Finland.\n\nBy accessing or using AnimLearn, you agree to these Terms of Service. If you are using AnimLearn as part of a school or institution, your institution may have a separate agreement with us that takes precedence.`,
      },
      {
        title: '2. Who Can Use AnimLearn',
        body: 'AnimLearn is intended for use in educational settings. There are two user roles:',
        bullets: [
          'Teachers — can upload syllabi, create courses, generate video content, manage assignments, and view student analytics.',
          'Students — can watch videos, complete assignments, and practise with adaptive exercises within courses they are enrolled in.',
        ],
        body2: 'If you are under 16 years old, you may only use AnimLearn under the supervision of a teacher or school administrator. Students do not create accounts independently — they are enrolled by teachers.',
      },
      {
        title: '3. Acceptable Use',
        body: 'You agree to use AnimLearn only for lawful educational purposes. You must not:',
        bullets: [
          'Upload content that is illegal, offensive, defamatory, or infringes third-party intellectual property rights',
          'Attempt to access another user\'s account or data without authorisation',
          'Reverse-engineer, scrape, or abuse the AnimLearn API or infrastructure',
          'Use AnimLearn to generate content for commercial resale without written permission',
          'Enter personal data of third parties (e.g. student names) into AI prompt fields — use anonymised or fictional names in examples',
        ],
      },
      {
        title: '4. AI-Generated Content Disclaimer',
        body: 'AnimLearn uses artificial intelligence to generate educational videos, explanations, and practice questions. **AI-generated content may contain errors, inaccuracies, or omissions.**',
        bullets: [
          'All AI-generated content should be verified against authoritative sources before being used for assessment or grading',
          'Teachers are responsible for reviewing content before sharing it with students',
          'AnimLearn does not guarantee that generated content is accurate, complete, or up to date',
          'Do not use AnimLearn as the sole basis for medical, legal, financial, or safety-critical decisions',
        ],
      },
      {
        title: '5. Teacher Responsibilities',
        body: 'If you are using AnimLearn as a teacher, you are responsible for:',
        bullets: [
          'Ensuring that student enrolment and data processing comply with your school\'s data protection policy and applicable law',
          'Reviewing AI-generated video content for accuracy before making it available to students',
          'Not uploading copyrighted content in a way that violates the publisher\'s terms',
          'Managing course access and ensuring only enrolled students can access course material',
        ],
      },
      {
        title: '6. Intellectual Property',
        body: '**Your content:** You retain ownership of the syllabi, documents, and prompts you upload. By uploading content, you grant nordX Labs Oy a limited licence to process and store that content for the purpose of providing the service.\n\n**Generated content:** AI-generated videos and materials may be used by you for educational purposes. You may not resell or commercially distribute generated content without our prior written consent.\n\n**AnimLearn platform:** The AnimLearn platform, branding, and underlying technology are owned by nordX Labs Oy. You may not copy, reproduce, or create derivative works from the platform.',
      },
      {
        title: '7. Privacy',
        body: 'Our collection and use of personal data is described in our Privacy Policy (/privacy-policy), which forms part of these Terms of Service.',
      },
      {
        title: '8. Service Availability',
        body: 'We aim to provide a reliable service, but we do not guarantee uninterrupted availability. AnimLearn is provided "as is" and we reserve the right to modify, suspend, or discontinue the service at any time, with reasonable notice where possible.\n\nVideo generation involves third-party AI services and may occasionally fail due to technical issues outside our control.',
      },
      {
        title: '9. Limitation of Liability',
        body: 'To the fullest extent permitted by Finnish law, nordX Labs Oy is not liable for:',
        bullets: [
          'Inaccuracies in AI-generated educational content',
          'Loss of data due to technical failures',
          'Indirect or consequential damages arising from use of the service',
          'Actions taken by users based on AI-generated content without independent verification',
        ],
      },
      {
        title: '10. Account Termination',
        body: 'You may delete your account at any time via Settings → Delete Account. This will permanently remove your account and all associated data.\n\nWe reserve the right to suspend or terminate accounts that violate these Terms of Service, with notice where practicable.',
      },
      {
        title: '11. Governing Law',
        body: 'These Terms of Service are governed by the laws of Finland. Any disputes arising from these terms shall be resolved in the courts of Finland.',
      },
      {
        title: '12. Changes to These Terms',
        body: 'We may update these terms from time to time. When we do, we will update the "Last updated" date. Material changes will be communicated via the application or by email. Continued use of AnimLearn after changes take effect constitutes acceptance of the updated terms.',
      },
      {
        title: '13. Contact',
        body: `For questions about these Terms of Service, contact:\n**${CONTROLLER}**\nEmail: ${CONTACT}`,
      },
    ],
  },

  fi: {
    title: 'Käyttöehdot',
    updated: `Päivitetty viimeksi: ${LAST_UPDATED}`,
    back: 'Takaisin',
    sections: [
      {
        title: '1. Tietoa AnimLearnista',
        body: `AnimLearn on tekoälypohjainen oppimisalusta, joka tuottaa animoituja opetusvideoja ja adaptiivisia harjoitustehtäviä. Palvelun tarjoaa **${CONTROLLER}** (Y-tunnus: ${BUSINESS_ID}), joka on rekisteröity Suomessa.\n\nKäyttämällä AnimLearnia hyväksyt nämä käyttöehdot. Jos käytät AnimLearnia osana koulua tai oppilaitosta, oppilaitoksellasi voi olla kanssamme erillinen sopimus, joka on etusijalla näihin ehtoihin nähden.`,
      },
      {
        title: '2. Ketkä voivat käyttää AnimLearnia',
        body: 'AnimLearn on tarkoitettu käytettäväksi oppimisympäristöissä. Käyttäjärooleja on kaksi:',
        bullets: [
          'Opettajat — voivat ladata oppimateriaaleja, luoda kursseja, tuottaa videosisältöä, hallita tehtäviä ja tarkastella opiskelijaanalytiikkaa.',
          'Opiskelijat — voivat katsella videoita, suorittaa tehtäviä ja harjoitella adaptiivisilla tehtävillä kursseilla, joille he ovat ilmoittautuneet.',
        ],
        body2: 'Jos olet alle 16-vuotias, voit käyttää AnimLearnia vain opettajan tai koulun ylläpitäjän valvonnassa. Opiskelijat eivät luo tilejä itsenäisesti — opettajat ilmoittavat heidät kursseille.',
      },
      {
        title: '3. Sallittu käyttö',
        body: 'Sitoudut käyttämään AnimLearnia ainoastaan laillisiin koulutustarkoituksiin. Et saa:',
        bullets: [
          'Ladata sisältöä, joka on laitonta, loukkaavaa, herjaavaa tai loukkaa kolmansien osapuolten immateriaalioikeuksia',
          'Yrittää päästä toisen käyttäjän tilille tai tietoihin ilman lupaa',
          'Käänteismallintaa, kaapia tai väärinkäyttää AnimLearnin rajapintaa tai infrastruktuuria',
          'Käyttää AnimLearnia sisällön tuottamiseen kaupalliseen jälleenmyyntiin ilman kirjallista lupaa',
          'Syöttää kolmansien osapuolten henkilötietoja (esim. opiskelijoiden nimiä) tekoälyprompteihin — käytä esimerkeissä anonymisoituja tai kuvitteellisia nimiä',
        ],
      },
      {
        title: '4. Tekoälysisällön vastuuvapauslauseke',
        body: 'AnimLearn käyttää tekoälyä opetusvideoden, selitysten ja harjoitustehtävien tuottamiseen. **Tekoälysisältö voi sisältää virheitä, epätarkkuuksia tai puutteita.**',
        bullets: [
          'Kaikki tekoälysisältö tulee varmistaa luotettavista lähteistä ennen käyttöä arvioinnissa tai arvosanoissa',
          'Opettajat vastaavat sisällön tarkistamisesta ennen sen jakamista opiskelijoille',
          'AnimLearn ei takaa, että tuotettu sisältö on täsmällistä, täydellistä tai ajantasaista',
          'Älä käytä AnimLearnia ainoana perustana lääketieteellisille, oikeudellisille, taloudellisille tai turvallisuuskriittisille päätöksille',
        ],
      },
      {
        title: '5. Opettajan vastuut',
        body: 'Jos käytät AnimLearnia opettajana, olet vastuussa seuraavista:',
        bullets: [
          'Sen varmistaminen, että opiskelijoiden ilmoittautuminen ja tietojen käsittely ovat koulusi tietosuojakäytännön ja sovellettavan lain mukaisia',
          'Tekoälygeneroitujen videosisältöjen tarkkuuden tarkistaminen ennen niiden saattamista opiskelijoiden saataville',
          'Tekijänoikeuksilla suojatun sisällön lataamatta jättäminen tavalla, joka rikkoo julkaisijan ehtoja',
          'Kurssin pääsyn hallinta ja sen varmistaminen, että vain ilmoittautuneet opiskelijat voivat käyttää kurssimateriaalia',
        ],
      },
      {
        title: '6. Immateriaalioikeudet',
        body: '**Sinun sisältösi:** Säilytät omistajuuden lataamistasi oppimateriaaleista, asiakirjoista ja prompteista. Lataamalla sisältöä myönnät nordX Labs Oy:lle rajoitetun lisenssin käsitellä ja tallentaa sisältöä palvelun tarjoamista varten.\n\n**Generoitu sisältö:** Voit käyttää tekoälygeneroituja videoita ja materiaaleja opetustarkoituksiin. Et saa myydä tai jaella kaupallisesti generoitua sisältöä ilman kirjallista lupaامه.\n\n**AnimLearn-alusta:** AnimLearn-alusta, tuotemerkit ja taustateknologia ovat nordX Labs Oy:n omaisuutta. Et saa kopioida, jäljentää tai luoda johdannaisteoksia alustasta.',
      },
      {
        title: '7. Tietosuoja',
        body: 'Henkilötietojen keräämistä ja käyttöä kuvataan tietosuojakäytännössämme (/privacy-policy), joka on osa näitä käyttöehtoja.',
      },
      {
        title: '8. Palvelun saatavuus',
        body: 'Pyrimme tarjoamaan luotettavan palvelun, mutta emme takaa keskeytyksetöntä saatavuutta. AnimLearn tarjotaan "sellaisena kuin se on" ja pidätämme oikeuden muuttaa, keskeyttää tai lopettaa palvelua milloin tahansa kohtuullisella ilmoitusajalla.\n\nVideontuotanto edellyttää kolmansien osapuolten tekoälypalveluja, ja se voi ajoittain epäonnistua teknisistä syistä, jotka ovat hallintamme ulkopuolella.',
      },
      {
        title: '9. Vastuunrajoitus',
        body: 'Suomen lain sallimassa laajuudessa nordX Labs Oy ei ole vastuussa:',
        bullets: [
          'Tekoälygeneroidun opetussisällön epätarkkuuksista',
          'Tietojen menettämisestä teknisten vikojen vuoksi',
          'Välillisistä vahingoista tai seuraamuksellisista vahingoista, jotka johtuvat palvelun käytöstä',
          'Toimista, jotka käyttäjät toteuttavat tekoälysisällön perusteella ilman itsenäistä varmentamista',
        ],
      },
      {
        title: '10. Tilin sulkeminen',
        body: 'Voit poistaa tilisi milloin tahansa kohdasta Asetukset → Poista tili. Tämä poistaa pysyvästi tilisi ja kaikki siihen liittyvät tiedot.\n\nPidätämme oikeuden keskeyttää tai sulkea tilejä, jotka rikkovat näitä käyttöehtoja, ilmoituksella aina kun se on mahdollista.',
      },
      {
        title: '11. Sovellettava laki',
        body: 'Näihin käyttöehtoihin sovelletaan Suomen lakia. Näitä ehtoja koskevat riidat ratkaistaan Suomen tuomioistuimissa.',
      },
      {
        title: '12. Muutokset näihin ehtoihin',
        body: 'Saatamme päivittää näitä ehtoja ajoittain. Päivityksen yhteydessä muutamme sivun yläosassa olevaa päivämäärää. Merkittävistä muutoksista ilmoitetaan sovelluksen kautta tai sähköpostitse. AnimLearnin jatkuva käyttö muutosten jälkeen merkitsee päivitettyjen ehtojen hyväksymistä.',
      },
      {
        title: '13. Yhteystiedot',
        body: `Käyttöehtoja koskevissa kysymyksissä ota yhteyttä:\n**${CONTROLLER}**\nSähköposti: ${CONTACT}`,
      },
    ],
  },

  sv: {
    title: 'Användarvillkor',
    updated: `Senast uppdaterad: ${LAST_UPDATED}`,
    back: 'Tillbaka',
    sections: [
      {
        title: '1. Om AnimLearn',
        body: `AnimLearn är en AI-driven utbildningsplattform som genererar animerade lärovideor och adaptiva övningsuppgifter. Den drivs av **${CONTROLLER}** (FO-nummer: ${BUSINESS_ID}), registrerat i Finland.\n\nGenom att använda AnimLearn godkänner du dessa användarvillkor. Om du använder AnimLearn som en del av en skola eller institution kan din institution ha ett separat avtal med oss som har företräde.`,
      },
      {
        title: '2. Vem kan använda AnimLearn',
        body: 'AnimLearn är avsett för användning i utbildningsmiljöer. Det finns två användarroller:',
        bullets: [
          'Lärare — kan ladda upp kursplaner, skapa kurser, generera videoinnehåll, hantera uppgifter och visa elevanalys.',
          'Elever — kan titta på videor, slutföra uppgifter och öva med adaptiva övningar inom kurser de är inskrivna i.',
        ],
        body2: 'Om du är under 16 år får du endast använda AnimLearn under överinseende av en lärare eller skoladministratör. Elever skapar inte konton självständigt — de skrivs in av lärare.',
      },
      {
        title: '3. Tillåten användning',
        body: 'Du samtycker till att använda AnimLearn endast för lagliga utbildningsändamål. Du får inte:',
        bullets: [
          'Ladda upp innehåll som är olagligt, stötande, ärekränkande eller kränker tredje parts immateriella rättigheter',
          'Försöka komma åt en annan användares konto eller data utan tillstånd',
          'Bakåtkompilera, skrapa eller missbruka AnimLearns API eller infrastruktur',
          'Använda AnimLearn för att generera innehåll för kommersiell återförsäljning utan skriftligt tillstånd',
          'Ange personuppgifter för tredje part (t.ex. elevnamn) i AI-promptfält — använd anonymiserade eller fiktiva namn i exempel',
        ],
      },
      {
        title: '4. Ansvarsfriskrivning för AI-genererat innehåll',
        body: 'AnimLearn använder artificiell intelligens för att generera utbildningsvideor, förklaringar och övningsfrågor. **AI-genererat innehåll kan innehålla fel, felaktigheter eller utelämnanden.**',
        bullets: [
          'Allt AI-genererat innehåll bör verifieras mot auktoritativa källor innan det används för bedömning eller betygsättning',
          'Lärare ansvarar för att granska innehåll innan det delas med elever',
          'AnimLearn garanterar inte att genererat innehåll är korrekt, fullständigt eller aktuellt',
          'Använd inte AnimLearn som enda grund för medicinska, juridiska, finansiella eller säkerhetskritiska beslut',
        ],
      },
      {
        title: '5. Lärarens ansvar',
        body: 'Om du använder AnimLearn som lärare ansvarar du för:',
        bullets: [
          'Att säkerställa att elevinskrivning och databehandling följer din skolas dataskyddspolicy och tillämplig lag',
          'Att granska AI-genererat videoinnehåll för noggrannhet innan det görs tillgängligt för elever',
          'Att inte ladda upp upphovsrättsskyddat innehåll på ett sätt som bryter mot utgivarens villkor',
          'Att hantera kursåtkomst och säkerställa att endast inskrivna elever kan komma åt kursmaterial',
        ],
      },
      {
        title: '6. Immateriella rättigheter',
        body: '**Ditt innehåll:** Du behåller äganderätten till de kursplaner, dokument och promptar du laddar upp. Genom att ladda upp innehåll beviljar du nordX Labs Oy en begränsad licens att behandla och lagra det innehållet i syfte att tillhandahålla tjänsten.\n\n**Genererat innehåll:** AI-genererade videor och material får användas av dig för utbildningsändamål. Du får inte återförsälja eller kommersiellt distribuera genererat innehåll utan vårt skriftliga samtycke.\n\n**AnimLearn-plattformen:** AnimLearn-plattformen, varumärket och den underliggande tekniken ägs av nordX Labs Oy. Du får inte kopiera, reproducera eller skapa härledda verk från plattformen.',
      },
      {
        title: '7. Integritet',
        body: 'Vår insamling och användning av personuppgifter beskrivs i vår integritetspolicy (/privacy-policy), som utgör en del av dessa användarvillkor.',
      },
      {
        title: '8. Tjänstens tillgänglighet',
        body: 'Vi strävar efter att erbjuda en tillförlitlig tjänst, men vi garanterar inte oavbruten tillgänglighet. AnimLearn tillhandahålls "i befintligt skick" och vi förbehåller oss rätten att ändra, avbryta eller avveckla tjänsten när som helst med rimlig förvarning.\n\nVideogenerering involverar tredjepartsbaserade AI-tjänster och kan ibland misslyckas av tekniska skäl utanför vår kontroll.',
      },
      {
        title: '9. Ansvarsbegränsning',
        body: 'I den utsträckning som tillåts enligt finsk lag är nordX Labs Oy inte ansvarigt för:',
        bullets: [
          'Felaktigheter i AI-genererat utbildningsinnehåll',
          'Förlust av data på grund av tekniska fel',
          'Indirekta skador eller följdskador som uppstår från användning av tjänsten',
          'Åtgärder som användare vidtar baserat på AI-genererat innehåll utan oberoende verifiering',
        ],
      },
      {
        title: '10. Kontoavslutning',
        body: 'Du kan ta bort ditt konto när som helst via Inställningar → Ta bort konto. Detta tar permanent bort ditt konto och alla tillhörande data.\n\nVi förbehåller oss rätten att stänga av eller avsluta konton som bryter mot dessa användarvillkor, med varsel när det är möjligt.',
      },
      {
        title: '11. Tillämplig lag',
        body: 'Dessa användarvillkor regleras av finsk lag. Eventuella tvister som uppstår från dessa villkor ska lösas i finländska domstolar.',
      },
      {
        title: '12. Ändringar i dessa villkor',
        body: 'Vi kan uppdatera dessa villkor från tid till annan. När vi gör det uppdaterar vi datumet "Senast uppdaterad". Väsentliga ändringar kommuniceras via applikationen eller via e-post. Fortsatt användning av AnimLearn efter att ändringar träder i kraft utgör godkännande av de uppdaterade villkoren.',
      },
      {
        title: '13. Kontakt',
        body: `För frågor om dessa användarvillkor, kontakta:\n**${CONTROLLER}**\nE-post: ${CONTACT}`,
      },
    ],
  },
};

export default function TermsOfService() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const c = content[language] || content.en;

  return (
    <div style={{ minHeight: '100vh', background: '#f8f8fc', fontFamily: 'Inter, sans-serif' }}>
      {/* Header */}
      <div style={{ background: '#fff', borderBottom: '1px solid #ece8f5', padding: '1rem 2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button onClick={() => navigate(-1)}
            style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', background: 'none', border: 'none', cursor: 'pointer', color: '#5B35A0', fontWeight: 500, fontSize: '0.9rem' }}>
            <ArrowLeft size={16} /> {c.back}
          </button>
          <span style={{ color: '#ccc' }}>|</span>
          <span style={{ fontWeight: 700, color: '#1a1a2e', fontSize: '1rem' }}>AnimLearn — {c.title}</span>
        </div>
        <LanguageSwitcher />
      </div>

      <div style={{ maxWidth: 780, margin: '0 auto', padding: '3rem 2rem 5rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 800, color: '#1a1a2e', marginBottom: '0.4rem' }}>{c.title}</h1>
        <p style={{ color: '#888', fontSize: '0.88rem', marginBottom: '2.5rem' }}>{c.updated}</p>

        {c.sections.map((s, i) => (
          <Section key={i} title={s.title}>
            {s.body && <RichText text={s.body} />}
            {s.bullets && <ul style={{ paddingLeft: '1.4rem', lineHeight: 2, margin: '0.5rem 0' }}>{s.bullets.map((b, j) => <li key={j}>{b}</li>)}</ul>}
            {s.body2 && <RichText text={s.body2} style={{ marginTop: '0.75rem' }} />}
          </Section>
        ))}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: '2.5rem' }}>
      <h2 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#5B35A0', marginBottom: '0.75rem', borderBottom: '2px solid #ece8f5', paddingBottom: '0.4rem' }}>
        {title}
      </h2>
      <div style={{ color: '#333', fontSize: '0.93rem', lineHeight: 1.75 }}>
        {children}
      </div>
    </div>
  );
}

function RichText({ text, style }) {
  const parts = text.split(/(\*\*[^*]+\*\*|\n)/g);
  return (
    <p style={{ margin: '0.3rem 0', ...style }}>
      {parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) return <strong key={i}>{part.slice(2, -2)}</strong>;
        if (part === '\n') return <br key={i} />;
        return part;
      })}
    </p>
  );
}
